import { toast } from "sonner";
import { offlineStorage, PendingRecording } from './offline-storage-service';
import { distributedLockManager } from './distributed-lock-manager';
import { supabase } from '@/integrations/supabase/client';
import { localLogger } from '@/lib/local-logger';

// Enhanced error classification system
export enum RetryableErrorType {
  NETWORK_TIMEOUT = 'network_timeout',
  CONNECTION_LOST = 'connection_lost',
  SERVER_OVERLOAD = 'server_overload',
  RATE_LIMITED = 'rate_limited',
  TRANSIENT_FAILURE = 'transient_failure',
  ASSEMBLY_AI_BUSY = 'assembly_ai_busy',
  LONG_TRANSCRIPTION_PROCESSING = 'long_transcription_processing', // For very long recordings still being processed
  NON_RETRYABLE = 'non_retryable'
}

export interface RetryConfiguration {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  exponentialBase: number;
  jitterPercent: number;
  circuitBreakerThreshold: number;
  healthCheckInterval: number;
}

export interface RetryContext {
  recordingId: string;
  attempts: number;
  lastError?: string;
  errorType?: RetryableErrorType;
  nextAttemptTime?: number;
  backoffMultiplier?: number;
}

export interface SystemHealthMetrics {
  isHealthy: boolean;
  databaseConnected: boolean;
  edgeFunctionsAvailable: boolean;
  transcriptionServiceStatus: 'available' | 'degraded' | 'unavailable';
  lastSuccessfulTranscription?: number;
  consecutiveFailures: number;
  circuitBreakerOpen: boolean;
  responseTime: number;
  timestamp: number;
}

export class EnhancedRetryManager {
  private config: RetryConfiguration = {
    maxRetries: 10, // Increased from 5 to 10 for long recordings
    baseDelay: 2000, // 2 seconds
    maxDelay: 300000, // 5 minutes
    exponentialBase: 1.5,
    jitterPercent: 0.2,
    circuitBreakerThreshold: 5,
    healthCheckInterval: 60000 // 1 minute
  };

  private systemHealth: SystemHealthMetrics = {
    isHealthy: false,
    databaseConnected: false,
    edgeFunctionsAvailable: false,
    transcriptionServiceStatus: 'unavailable',
    consecutiveFailures: 0,
    circuitBreakerOpen: false,
    responseTime: 0,
    timestamp: 0
  };

  private lastHealthCheck = 0;
  private consecutiveFailures = 0;
  private circuitBreakerOpenUntil = 0;

  // Enhanced error classification
  classifyError(error: any): RetryableErrorType {
    const message = error?.message?.toLowerCase() || error?.toString()?.toLowerCase() || '';

    // Long transcription processing (not an error, just needs more time)
    if (message.includes('still processing') || message.includes('transcription is taking longer')) {
      return RetryableErrorType.LONG_TRANSCRIPTION_PROCESSING;
    }

    // Network-related errors (but exclude 406 which is often an RLS policy issue, not transient)
    if (message.includes('timeout') || message.includes('network') || message.includes('fetch')) {
      // 406 Not Acceptable is usually a schema/RLS issue, not a transient network error
      if (message.includes('406') || message.includes('not acceptable')) {
        return RetryableErrorType.NON_RETRYABLE;
      }
      return RetryableErrorType.NETWORK_TIMEOUT;
    }

    if (message.includes('connection') || message.includes('offline') || message.includes('disconnect')) {
      return RetryableErrorType.CONNECTION_LOST;
    }

    // Server errors
    if (message.includes('500') || message.includes('502') || message.includes('503') || message.includes('504')) {
      return RetryableErrorType.SERVER_OVERLOAD;
    }

    // Rate limiting
    if (message.includes('429') || message.includes('rate limit') || message.includes('too many requests')) {
      return RetryableErrorType.RATE_LIMITED;
    }

    // Assembly AI specific
    if (message.includes('assembly') || message.includes('transcription busy') || message.includes('processing queue full')) {
      return RetryableErrorType.ASSEMBLY_AI_BUSY;
    }

    // Non-retryable errors - be specific to avoid wrongly classifying transient server errors
    // Empty/missing audio is not retryable - it requires user action (re-record)
    if (message.includes('unauthorized') || message.includes('forbidden') ||
      message.includes('not found') || message.includes('401') ||
      message.includes('403') || message.includes('404') ||
      message.includes('no speech') || message.includes('no detectable speech') ||
      message.includes('no text was generated') || message.includes('too short') ||
      message.includes('silent') || message.includes('audio blob is not available') ||
      message.includes('recording contains no audio') || message.includes('empty audio') ||
      message.includes('file too large') || message.includes('invalid audio') ||
      message.includes('invalid base64') || message.includes('invalid action')) {
      return RetryableErrorType.NON_RETRYABLE;
    }

    // Default to transient failure
    return RetryableErrorType.TRANSIENT_FAILURE;
  }

  // Intelligent backoff calculation
  calculateRetryDelay(context: RetryContext, errorType: RetryableErrorType): number {
    const { attempts } = context;

    // Special handling for different error types
    let baseDelay = this.config.baseDelay;
    let exponentialBase = this.config.exponentialBase;

    switch (errorType) {
      case RetryableErrorType.LONG_TRANSCRIPTION_PROCESSING:
        baseDelay = 60000; // Start with 60 seconds for long transcriptions
        exponentialBase = 1.2; // Very slow growth - these just need time
        break;
      case RetryableErrorType.RATE_LIMITED:
        baseDelay = 30000; // Start with 30 seconds for rate limiting
        exponentialBase = 2; // More aggressive backoff
        break;
      case RetryableErrorType.ASSEMBLY_AI_BUSY:
        baseDelay = 60000; // Start with 1 minute for busy service
        exponentialBase = 1.8;
        break;
      case RetryableErrorType.SERVER_OVERLOAD:
        baseDelay = 10000; // Start with 10 seconds for server overload
        exponentialBase = 2;
        break;
      case RetryableErrorType.NETWORK_TIMEOUT:
        baseDelay = 5000; // Quick retry for network issues
        exponentialBase = 1.3;
        break;
      case RetryableErrorType.CONNECTION_LOST:
        baseDelay = 3000; // Very quick retry for connection issues
        exponentialBase = 1.2;
        break;
    }

    // Calculate exponential backoff
    const exponentialDelay = baseDelay * Math.pow(exponentialBase, attempts - 1);
    const cappedDelay = Math.min(exponentialDelay, this.config.maxDelay);

    // Add jitter to prevent thundering herd
    const jitter = cappedDelay * this.config.jitterPercent * (Math.random() - 0.5);
    const finalDelay = Math.max(1000, cappedDelay + jitter); // Minimum 1 second

    console.log(`Calculated retry delay for ${errorType}: ${finalDelay}ms (attempt ${attempts})`);
    return finalDelay;
  }

  // Enhanced system health monitoring
  async performHealthCheck(): Promise<SystemHealthMetrics> {
    const now = Date.now();

    // Return cached result if recent
    if (this.systemHealth.timestamp > 0 &&
      (now - this.systemHealth.timestamp) < this.config.healthCheckInterval) {
      return this.systemHealth;
    }

    const startTime = Date.now();
    const healthMetrics: SystemHealthMetrics = {
      isHealthy: false,
      databaseConnected: false,
      edgeFunctionsAvailable: false,
      transcriptionServiceStatus: 'unavailable',
      consecutiveFailures: this.consecutiveFailures,
      circuitBreakerOpen: this.isCircuitBreakerOpen(),
      responseTime: 0,
      timestamp: now
    };

    try {
      console.log('🔍 Performing comprehensive health check...');

      // Test 1: Database connectivity
      const dbTest = await Promise.race([
        supabase.from('templates').select('count').limit(1),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Database timeout')), 15000))
      ]);

      if (dbTest && !(dbTest as any).error) {
        healthMetrics.databaseConnected = true;
        console.log('✅ Database connection healthy');
      } else {
        // Silently fail for database connection checks - this is expected before auth
        if (import.meta.env.DEV) {
          console.debug('Database connection check failed (expected before auth)');
        }
      }

      // Test 2: Edge function accessibility (optional - don't block on this)
      try {
        const edgeTest = await Promise.race([
          supabase.functions.invoke('transcribe', {
            body: { test: true, healthCheck: true }
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Edge function timeout')), 5000)) // Shorter timeout
        ]);

        if (edgeTest && !(edgeTest as any).error) {
          healthMetrics.edgeFunctionsAvailable = true;
          console.log('✅ Edge functions available');

          // Check transcription service status
          const edgeData = (edgeTest as any).data;
          if (edgeData?.transcriptionServiceReady) {
            healthMetrics.transcriptionServiceStatus = 'available';
          } else if (edgeData?.transcriptionServiceDegraded) {
            healthMetrics.transcriptionServiceStatus = 'degraded';
          } else {
            // If edge function responds but doesn't have health data, assume available
            healthMetrics.transcriptionServiceStatus = 'available';
          }
        } else {
          console.warn('⚠️ Edge functions check failed - will proceed anyway');
          // Assume available if edge function test fails (don't block processing)
          healthMetrics.edgeFunctionsAvailable = true;
          healthMetrics.transcriptionServiceStatus = 'available';
        }
      } catch (edgeError) {
        console.warn('⚠️ Edge function health check error - assuming available:', edgeError);
        // Don't block processing if edge function test fails
        healthMetrics.edgeFunctionsAvailable = true;
        healthMetrics.transcriptionServiceStatus = 'available';
      }

      // Determine overall health - only require database connectivity
      // Edge functions are tested but don't block processing
      healthMetrics.isHealthy = healthMetrics.databaseConnected;

      healthMetrics.responseTime = Date.now() - startTime;

      // Update circuit breaker state
      if (healthMetrics.isHealthy) {
        this.consecutiveFailures = 0;
        this.circuitBreakerOpenUntil = 0;
      } else {
        this.consecutiveFailures++;
        if (this.consecutiveFailures >= this.config.circuitBreakerThreshold) {
          this.circuitBreakerOpenUntil = now + (5 * 60 * 1000); // 5 minutes
          console.warn('🚫 Circuit breaker opened due to consecutive failures');
        }
      }

      this.systemHealth = healthMetrics;
      console.log(`Health check completed in ${healthMetrics.responseTime}ms:`, healthMetrics);

      return healthMetrics;

    } catch (error) {
      console.error('❌ Health check failed:', error);

      healthMetrics.responseTime = Date.now() - startTime;
      this.consecutiveFailures++;

      this.systemHealth = healthMetrics;
      return healthMetrics;
    }
  }

  // Circuit breaker pattern
  isCircuitBreakerOpen(): boolean {
    return Date.now() < this.circuitBreakerOpenUntil;
  }

  // Smart retry decision making
  async shouldRetry(
    recording: PendingRecording,
    error: any,
    currentAttempts: number
  ): Promise<{
    shouldRetry: boolean;
    nextAttemptTime?: number;
    reason: string;
  }> {
    // Check circuit breaker
    if (this.isCircuitBreakerOpen()) {
      return {
        shouldRetry: false,
        reason: 'Circuit breaker is open - system experiencing failures'
      };
    }

    // Check max retries
    if (currentAttempts >= this.config.maxRetries) {
      return {
        shouldRetry: false,
        reason: `Maximum retry attempts exceeded (${this.config.maxRetries})`
      };
    }

    // Classify error
    const errorType = this.classifyError(error);

    // Non-retryable errors
    if (errorType === RetryableErrorType.NON_RETRYABLE) {
      return {
        shouldRetry: false,
        reason: 'Error is not retryable (client error or invalid data)'
      };
    }

    // Check system health for certain error types
    if ([RetryableErrorType.SERVER_OVERLOAD, RetryableErrorType.ASSEMBLY_AI_BUSY].includes(errorType)) {
      const health = await this.performHealthCheck();
      if (!health.isHealthy) {
        return {
          shouldRetry: false,
          reason: 'System is unhealthy - deferring retry until health improves'
        };
      }
    }

    // Calculate next attempt time
    const context: RetryContext = {
      recordingId: recording.id,
      attempts: currentAttempts,
      lastError: error?.message,
      errorType
    };

    const delay = this.calculateRetryDelay(context, errorType);
    const nextAttemptTime = Date.now() + delay;

    return {
      shouldRetry: true,
      nextAttemptTime,
      reason: `Retryable ${errorType} - next attempt in ${Math.round(delay / 1000)}s`
    };
  }

  // Enhanced retry execution
  async scheduleRetry(
    recording: PendingRecording,
    error: any,
    currentAttempts: number
  ): Promise<boolean> {
    // ⚠️ CRITICAL: Prevent Retry Storm by checking if another tab is already retrying
    // If the recording is locked, skip scheduling a new retry since another instance is handling it
    try {
      const lockStatus = await distributedLockManager.isLocked(recording.id);
      if (lockStatus && lockStatus.processing_instance_id !== distributedLockManager.getInstanceId(recording.user_id)) {
        // Another tab is already processing/retrying this recording
        console.log(`🔒 Retry skipped - recording ${recording.id} already being retried by ${lockStatus.processing_instance_id}`);
        // Don't schedule new retry, let the other instance handle it
        return true; // Return true to suppress "retry failed" message
      }
    } catch (err) {
      // If we can't check lock status, proceed with normal retry logic
      console.warn('⚠️ Failed to check lock status for retry safeguard:', err);
    }

    const decision = await this.shouldRetry(recording, error, currentAttempts);

    if (!decision.shouldRetry) {
      console.log(`❌ Not retrying ${recording.id}: ${decision.reason}`);

      // Mark as permanent failure
      await offlineStorage.updateRecordingStatus(recording.id, 'error', currentAttempts);

      // Enhanced error reporting
      localLogger.setContext('retry_failure', {
        recordingId: recording.id,
        attempts: currentAttempts,
        reason: decision.reason,
        systemHealth: this.systemHealth
      });

      localLogger.captureError(error as Error, {
        component: 'retry_manager',
        operation: 'permanent_failure',
        metadata: { recording_id: recording.id }
      });

      toast.error(`Recording "${recording.title}" failed permanently: ${decision.reason}`);
      return false;
    }

    // Schedule the retry
    const nextAttempts = currentAttempts + 1;
    await offlineStorage.updateRecordingRetry(
      recording.id,
      'pending',
      nextAttempts,
      decision.nextAttemptTime!
    );

    const delayMs = decision.nextAttemptTime! - Date.now();
    const delayText = delayMs < 60000 ?
      `${Math.round(delayMs / 1000)} seconds` :
      `${Math.round(delayMs / 60000)} minutes`;

    console.log(`🔄 Scheduled retry for ${recording.id}: attempt ${nextAttempts} in ${delayText}`);

    // User-friendly notification
    toast.info(`Recording "${recording.title}" will retry in ${delayText} (attempt ${nextAttempts}/${this.config.maxRetries})`);

    // Enhanced tracking
    localLogger.addBreadcrumb(
      `Scheduled retry for ${recording.title}: attempt ${nextAttempts}`,
      'auth', // Using auth category as a placeholder or could use 'info'
      'info'
    );

    return true;
  }

  // Get retry statistics
  async getRetryStatistics(userId: string): Promise<{
    totalPending: number;
    awaitingRetry: number;
    permanentFailures: number;
    averageRetryCount: number;
    nextRetryTime?: number;
  }> {
    const recordings = await offlineStorage.getRecordings(userId);
    const now = Date.now();

    const pending = recordings.filter(r => r.status === 'pending');
    const awaitingRetry = pending.filter(r => r.nextAttempt && r.nextAttempt > now);
    const permanentFailures = recordings.filter(r => r.status === 'error');

    const totalAttempts = recordings.reduce((sum, r) => sum + (r.attempts || 0), 0);
    const averageRetryCount = recordings.length > 0 ? totalAttempts / recordings.length : 0;

    const nextRetry = awaitingRetry
      .map(r => r.nextAttempt!)
      .sort((a, b) => a - b)[0];

    return {
      totalPending: pending.length,
      awaitingRetry: awaitingRetry.length,
      permanentFailures: permanentFailures.length,
      averageRetryCount: Math.round(averageRetryCount * 100) / 100,
      nextRetryTime: nextRetry
    };
  }

  // Update configuration
  updateConfiguration(newConfig: Partial<RetryConfiguration>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('Updated retry configuration:', this.config);
  }

  // Get current system health
  getCurrentHealth(): SystemHealthMetrics {
    return this.systemHealth;
  }

  // Manual circuit breaker reset
  resetCircuitBreaker(): void {
    this.consecutiveFailures = 0;
    this.circuitBreakerOpenUntil = 0;
    console.log('Circuit breaker manually reset');
  }
}

export const retryManager = new EnhancedRetryManager();